import React, { useState, useEffect, useContext } from 'react';

import _, { Dictionary } from 'lodash';
import { addFavorite } from 'mintel-taskbar';

import {
  FavoritesProps,
  Favorite,
  FavoriteMetadata,
  UserAccessRestrictions,
} from '/types';
import useUserAccessRestrictions from '/utils/useUserAccessRestrictions';

import { FavoritesContext, PublishStatusContext } from './contexts';
import { useCwaAxios } from './cwaAxios';
import useTracking from './useTracking';

const FAVORITES_URL = '/api/favourites';

const FavoritesProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const { trackFavorite } = useTracking();
  const [favorites, setFavorites] = useState({} as Dictionary<Favorite>);
  const axios = useCwaAxios();

  const deleteFav = (id: string): void => {
    axios.delete(`${FAVORITES_URL}/${id}`);
    setFavorites(_.omit(favorites, id));
  };

  const addFav = ({
    id,
    tag,
    title,
    url,
    article_image_url,
  }: Favorite): void => {
    const newFav: Favorite = {
      id,
      tag,
      title,
      url,
      article_image_url,
      timestamp: Date.now(),
    };

    trackFavorite(tag);
    axios.put(`${FAVORITES_URL}/${id}`, newFav);
    setFavorites({ ...favorites, [id]: newFav });
  };

  const deleteTag = (tag: string): void => {
    const deleteIds = Object.values(favorites)
      .filter((fav) => fav.tag === tag)
      .map(({ id }) => id);
    Promise.all(deleteIds.map((id) => axios.delete(`${FAVORITES_URL}/${id}`)));
    setFavorites(_.omit(favorites, deleteIds));
  };

  const renameTag = (prev: string, next: string): void => {
    const newFavs = { ...favorites };
    const renamedFavs = Object.values(favorites)
      .filter(({ tag }) => tag === prev)
      .map((fav) => {
        const newFav = { ...fav, tag: next };
        newFavs[fav.id] = newFav;
        return newFav;
      });
    Promise.all(
      renamedFavs.map((fav) => axios.put(`${FAVORITES_URL}/${fav.id}`, fav)),
    );
    setFavorites(newFavs);
  };

  const isFavorited = (id: string, groupId?: string): boolean =>
    !!favorites[id] || (groupId && !!favorites[groupId]);

  const onFavorite = (articleData: FavoriteMetadata): void => {
    const group_id = articleData.group_id || '';
    const fav: FavoriteMetadata = {
      id: articleData.id,
      title: articleData.title,
      url: articleData.url,
      article_image_url: articleData.article_image_url,
    };
    if (isFavorited(fav.id)) deleteFav(fav.id);
    else if (isFavorited(group_id)) deleteFav(group_id);
    else addFavorite(fav);
  };

  const accessRestrictionsString = JSON.stringify(useUserAccessRestrictions());
  const publishStatus = useContext(PublishStatusContext);
  const isLongTermPreview = publishStatus === 'long_term_preview';

  useEffect(() => {
    const accessRestrictions = JSON.parse(
      accessRestrictionsString,
    ) as UserAccessRestrictions;
    if (
      accessRestrictions.isLoggedInUser &&
      !accessRestrictions.isLibraryLockdownUser &&
      !accessRestrictions.isAnonymousUser &&
      !isLongTermPreview
    ) {
      axios.get(FAVORITES_URL).then((data) => {
        const favs = _.get(data, 'data.data');
        setFavorites(_.keyBy(favs, 'id'));
      });
    }
  }, [axios, accessRestrictionsString, isLongTermPreview]);

  const providerValue: FavoritesProps = {
    isFavorited: isFavorited,
    onFavorite: onFavorite,
    favoritesPanelProps: {
      favorites: Object.values(favorites),
      onDeleteFavorite: deleteFav,
      onAddFavorite: (tag: string, favData: Favorite) =>
        addFav({ ...favData, tag }),
      onRenameFolder: renameTag,
      onDeleteFolder: deleteTag,
    },
  };

  return (
    <FavoritesContext.Provider value={providerValue}>
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesProvider;
